<template>
  <div class="ve-split">
    <!-- Left: list panel -->
    <div class="ve-list-panel">
      <div class="ve-list-header">
        <span class="ve-list-title">
          {{ items.length }} {{ items.length === 1 ? itemLabel : itemLabel + 's' }}
        </span>
        <button
          type="button"
          class="ve-add-btn"
          :class="{ 've-add-btn--active': resolvedAddActive }"
          @click="onAddClick"
        >+ Add</button>
      </div>
      <div class="ve-list-body">
        <div v-if="items.length === 0" class="ve-list-empty">No {{ itemLabel }} yet</div>
        <div
          v-for="(item, idx) in items"
          :key="item.id"
          class="ve-list-item"
          :class="resolvedItemClasses(item, idx)"
          :title="resolvedItemTitle(item, idx)"
          @click="onItemClick(item, idx)"
        >
          <slot name="list-item" :item="item" :idx="idx">
            <img v-if="item.thumbnailUrl" :src="item.thumbnailUrl" class="ve-list-thumb" />
            <span class="ve-list-name">{{ item.name }}</span>
          </slot>
          <button type="button" class="ve-list-remove" title="Remove" @click.stop="onRemove(item, idx)">✕</button>
        </div>
      </div>
    </div>

    <!-- Right: detail panel -->
    <div class="ve-detail-panel">
      <template v-if="$scopedSlots.detail">
        <slot name="detail" />
      </template>
      <template v-else>
        <!-- Placeholder: no mode active yet -->
        <template v-if="mode === null">
          <div style="padding: 3rem 1rem; text-align: center">
            <p style="color: #8b949e; font-size: 0.88rem">
              Press <strong style="color: #e6edf3">+Add</strong> to add your first {{ itemLabel }}.
            </p>
          </div>
        </template>

        <!-- Add mode -->
        <template v-else-if="mode === 'add'">
          <slot name="add-panel" />
        </template>

        <!-- Edit mode -->
        <template v-else-if="mode === 'edit' && selectedItem">
          <slot name="edit-panel" :item="selectedItem" :idx="selectedIdx" :open-lightbox="openLightbox" />
        </template>
      </template>
    </div>
    <!-- Lightbox overlay -->
    <div v-if="lightboxUrl" class="ve-lightbox" @click="lightboxUrl = null">
      <img :src="lightboxUrl" class="ve-lightbox-img" @click.stop />
      <button type="button" class="ve-lightbox-close" @click="lightboxUrl = null">✕</button>
    </div>
  </div>
</template>

<script>
export default {
  name: 'EditorShell',

  props: {
    items: { type: Array, default: () => [] },
    itemLabel: { type: String, default: 'item' },
    // Override Add button active state (null = use internal mode === 'add')
    addBtnActive: { type: Boolean, default: null },
    // Override item row classes: fn(item, idx) => Object
    getItemClasses: { type: Function, default: null },
    // Override item row title: fn(item, idx) => String
    getItemTitle: { type: Function, default: null },
  },

  data() {
    return {
      mode: null,
      selectedIdx: null,
      lightboxUrl: null,
    };
  },

  computed: {
    selectedItem() {
      if (this.selectedIdx === null) return null;
      return this.items[this.selectedIdx] || null;
    },
    resolvedAddActive() {
      return this.addBtnActive !== null ? this.addBtnActive : this.mode === 'add';
    },
  },

  methods: {
    // ── Public API (callable via $refs) ──────────────────────────────────────

    startAdd() {
      this.mode = 'add';
      this.selectedIdx = null;
    },

    selectIdx(idx) {
      this.mode = 'edit';
      this.selectedIdx = idx;
    },

    /**
     * Called by the parent when it removes an item from its data without going
     * through the shell's remove button (e.g. SkinGrid toggle-off). Adjusts
     * selectedIdx so the shell stays consistent.
     */
    notifyRemove(idx) {
      if (this.mode === 'edit') {
        if (this.selectedIdx === idx) {
          this.mode = 'add';
          this.selectedIdx = null;
        } else if (this.selectedIdx > idx) {
          this.selectedIdx--;
        }
      }
    },

    // ── Internal handlers ────────────────────────────────────────────────────

    onAddClick() {
      this.startAdd();
      this.$emit('add-click');
    },

    openLightbox(url) {
      if (url) this.lightboxUrl = url;
    },

    onItemClick(item, idx) {
      this.selectIdx(idx);
      this.$emit('item-click', item, idx);
    },

    onRemove(item, idx) {
      this.notifyRemove(idx);
      this.$emit('remove', item, idx);
    },

    resolvedItemClasses(item, idx) {
      if (this.getItemClasses) return this.getItemClasses(item, idx);
      return {
        've-list-item--selected': this.mode === 'edit' && this.selectedIdx === idx,
      };
    },

    resolvedItemTitle(item, idx) {
      if (this.getItemTitle) return this.getItemTitle(item, idx);
      return '';
    },
  },
};
</script>

<style>
/* ── Two-panel layout shared by all visual editors ── */
.ve-split { display: flex; width: 100%; min-height: 400px; }
.ve-list-panel { flex: 0 0 220px; min-width: 180px; display: flex; flex-direction: column; border: 1px solid #30363d; border-radius: 8px 0 0 8px; overflow: hidden; background: #161b22; }
.ve-list-header { padding: 0.55rem 0.75rem; border-bottom: 1px solid #30363d; display: flex; align-items: center; justify-content: space-between; background: #21262d; flex-shrink: 0; }
.ve-list-title { font-size: 0.74rem; color: #8b949e; font-weight: 600; text-transform: uppercase; letter-spacing: 0.04em; }
.ve-add-btn { padding: 0.25rem 0.6rem; background: #3fb950; color: #fff; border: none; border-radius: 4px; font-size: 0.78rem; font-weight: 600; cursor: pointer; transition: background 0.15s; }
.ve-add-btn:hover { background: #44c461; }
.ve-add-btn--active { background: #44c461; box-shadow: 0 0 0 2px rgba(63,185,80,0.3); }
.ve-list-body { flex: 1; overflow-y: auto; padding: 0.3rem 0; }
.ve-list-empty { padding: 2rem 0.75rem; text-align: center; font-size: 0.8rem; color: #484f58; }
.ve-list-item { display: flex; align-items: center; gap: 0.5rem; padding: 0.35rem 0.75rem; cursor: pointer; transition: background 0.1s; border-left: 3px solid transparent; }
.ve-list-item:hover { background: rgba(255,255,255,0.04); }
.ve-list-item--selected { background: rgba(31,111,235,0.12); border-left-color: #1f6feb; }
.ve-list-item--disabled { cursor: default; opacity: 0.5; pointer-events: none; }
.ve-list-thumb { width: 30px; height: 30px; object-fit: contain; border-radius: 3px; flex-shrink: 0; background: #0d1117; border: 1px solid #21262d; }
.ve-list-name { flex: 1; font-size: 0.83rem; color: #e6edf3; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.ve-list-remove { background: none; border: none; color: #484f58; cursor: pointer; padding: 1px 4px; font-size: 0.82rem; border-radius: 3px; flex-shrink: 0; line-height: 1; transition: color 0.1s; }
.ve-list-remove:hover { color: #f85149; }
.ve-detail-panel { flex: 1; min-width: 0; border: 1px solid #30363d; border-left: none; border-radius: 0 8px 8px 0; overflow-y: auto; padding: 1rem 1.25rem; background: #0d1117; }
.ve-hint { font-size: 0.82rem; color: #8b949e; margin: 0 0 0.75rem 0; }
.ve-edit-form { max-width: 480px; }
.ve-edit-row-head { display: flex; align-items: center; gap: 1rem; margin-bottom: 1.25rem; padding-bottom: 1rem; border-bottom: 1px solid #21262d; }
.ve-edit-thumb { width: 64px; height: 64px; object-fit: contain; border-radius: 6px; border: 1px solid #30363d; background: #161b22; flex-shrink: 0; cursor: zoom-in; transition: opacity 0.15s; }
.ve-edit-thumb:hover { opacity: 0.8; }
.ve-edit-thumb-wide { height: 70px; border-radius: 6px; border: 1px solid #30363d; background: #161b22; flex-shrink: 0; cursor: zoom-in; transition: opacity 0.15s; }
.ve-edit-thumb-wide:hover { opacity: 0.8; }
.ve-edit-skin-name { font-weight: 600; font-size: 0.95rem; color: #e6edf3; }
.ve-edit-hint { font-size: 0.78rem; color: #8b949e; margin-top: 0.2rem; }
.ve-edit-group { margin-bottom: 1rem; }
.ve-edit-label { display: block; font-size: 0.8rem; font-weight: 600; color: #8b949e; text-transform: uppercase; letter-spacing: 0.04em; margin-bottom: 0.35rem; }
.ve-lightbox { position: fixed; inset: 0; background: rgba(0,0,0,0.85); display: flex; align-items: center; justify-content: center; z-index: 9999; cursor: zoom-out; }
.ve-lightbox-img { max-width: 90vw; max-height: 90vh; object-fit: contain; border-radius: 8px; box-shadow: 0 8px 40px rgba(0,0,0,0.6); cursor: default; }
.ve-lightbox-close { position: fixed; top: 1.25rem; right: 1.5rem; background: rgba(255,255,255,0.1); border: none; color: #fff; font-size: 1.4rem; width: 2.2rem; height: 2.2rem; border-radius: 50%; cursor: pointer; display: flex; align-items: center; justify-content: center; transition: background 0.15s; }
.ve-lightbox-close:hover { background: rgba(255,255,255,0.2); }
</style>
